import { Injectable, inject } from '@angular/core';

import { TranslateService } from '@ngx-translate/core';

import { environment } from '../../../environments/environment';
import * as amplitude from '@amplitude/analytics-browser';
import { AccessControlService } from './access-control.service';
import { PlantContextStore } from './plant-context.store';
import { UserIdentityStore } from './user-identity.store';

const base = 'Base';
const report = 'Relatórios';
const camera = 'Câmeras';
const sat = 'Detecção Satelital';
const fm = 'Recursos';
const risk = 'Risco';
const historical_data = 'Dados Históricos';
const bio = 'Biodiversidade';
const deforestation = 'Desmatamento';
const ugm = 'umgrauemeio';
const settings = 'Minha Conta';
const users = 'Usuários';
const monitored_area = 'Área monitorada';
const dashboard = 'Dashboard';

const amplitudePrdK = 'cade42308bd4aa9011f79945f16a5417';
const amplitudeDevK = '35b1fca2b7553566dd31daa1c6ce0167';

@Injectable({
  providedIn: 'root',
})
export class AmplitudeService {
  private accessControl = inject(AccessControlService);
  private plantStore = inject(PlantContextStore);
  private userStore = inject(UserIdentityStore);
  private translate = inject(TranslateService);

  private amplitudeKey: string;
  private isInitialized = false;

  constructor() {
    this.initKey();
  }

  // [to do - mover dicionários para fora do serviço]
  public groupName = {
    base: base, // camada
    reports: report, // camada e painel
    visibility: camera, // camada
    cams: camera, // painel
    sat: sat, //camada e painel
    fm: fm, // camada
    brigades: fm, // painel
    risk: risk, // camada
    weather: risk, // painel
    historicalData: historical_data, // camada
    'historical-data': historical_data, // painel
    bio: bio, // camada e painel
    deforestation: deforestation, // camada e painel
    ugm: ugm,
    settings: settings,
    users: users,
    dashboard: dashboard,
    clientRoi: monitored_area, // camada
  };

  public layerGroupIdToGroupName = {
    100: base, // MAP.BASEMAP
    1: monitored_area, // MAP.TALHAO_LAYERS
    6: monitored_area, // MAP.LINE_LAYERS
    22: monitored_area, // MAP.ADMINISTRATIVE_LIMITS
    23: monitored_area, // MAP.PROTECTED_AREAS
    3: report, // MAP.REPORTS
    24: report, // REP.SCAR.IMAGE_GROUP
    25: report, // REP.SCAR.POLYGON_LAYER_GROUP
    26: report, // Aria
    2: camera, // MAP.INFRASTRUCTURE
    7: camera, // MAP.VISIBILITY_MAP
    4: sat, // SAT.ACTIVE_FIRE_GROUP
    10: sat, // SAT.DETECTION_HISTORY_GROUP
    14: sat, // SAT.ORBIT_TRACK_GROUP
    18: sat, // SAT.ROI_GROUP
    21: sat, // SAT.HOTSPOT_WORLD_GROUP
    8: fm, // MAP.FLEET_TRACKING
    11: fm, // MAP.POINTS_OF_INTEREST
    5: risk, // MAP.FIRE_RISK
    80: risk, // MAP.CLIMATE
    82: risk, // MAP.WEATHER_STATIONS
    9: historical_data, // HD.HOTSPOT_GROUP
    15: historical_data, // HD.SCAR_YEAR_GROUP
    27: historical_data, // HD.SCAR_MONTH_GROUP
    16: deforestation, // DEF.DEFORESTATION
    37: deforestation, // DEF.FAST_ALERTS
    13: bio, // MAP.BIODIVERSITY
    83: bio, // MAP.CAMERA_TRAP
    0: ugm, // umgrauemeio
    17: ugm, // GeoLeads
    19: ugm, // Infraestrutura
    20: ugm, // Preservação
    81: ugm, // Risco de incêndio - outros
  };

  public resourceName = {
    brigade: 'Brigada',
    poi: 'Ponto de interesse',
    contact: 'Contato',
    onca: 'Onça',
  };

  initKey() {
    if (environment.production) {
      this.amplitudeKey = amplitudePrdK;
    } else {
      this.amplitudeKey = amplitudeDevK;
    }
  }

  sendEvent(eventName: string, eventProperties?) {
    this.ensureInitialized();

    const safeEventProperties = this.sanitizeEventProperties(eventProperties);
    if (safeEventProperties) {
      amplitude.track(eventName, safeEventProperties);
    } else {
      amplitude.track(eventName);
    }
  }

  setUserProperties() {
    this.ensureInitialized();
    const identify = new amplitude.Identify();
    try {
      identify
        .set('Cliente', this.plantStore.nomeCliente())
        .set('Planta', this.plantStore.nomePlanta())
        .set('ID Planta', this.plantStore.idPlanta())
        .set('Usuário', this.userStore.username())
        .set('E-mail', this.userStore.email())
        .set('UGM', this.isUgm())
        .set('ID Tipo Usuário', this.userStore.idRole())
        .set('Versão', environment.version);
      amplitude.identify(identify);
    } catch {
      // Falha de analytics não pode interromper o fluxo do usuário
    }
  }

  private ensureInitialized() {
    if (this.isInitialized) {
      return;
    }

    amplitude.init(this.amplitudeKey, this.userStore.userId(), {
      defaultTracking: false,
    });
    this.isInitialized = true;
  }

  private sanitizeEventProperties(eventProperties: any) {
    if (!eventProperties || typeof eventProperties !== 'object') {
      return eventProperties;
    }

    const visited = new WeakSet();

    const sanitize = (value: any): any => {
      if (
        value === null ||
        typeof value === 'string' ||
        typeof value === 'number' ||
        typeof value === 'boolean'
      ) {
        return value;
      }

      if (value instanceof Date) {
        return value.toISOString();
      }

      if (typeof value === 'bigint') {
        return value.toString();
      }

      if (Array.isArray(value)) {
        return value.map((item) => sanitize(item)).filter((item) => item !== undefined);
      }

      if (typeof value !== 'object') {
        return undefined;
      }

      if (visited.has(value)) {
        return '[Circular]';
      }
      visited.add(value);

      const sanitizedObject = {};
      Object.keys(value).forEach((key) => {
        const sanitizedValue = sanitize(value[key]);
        if (sanitizedValue !== undefined) {
          sanitizedObject[key] = sanitizedValue;
        }
      });

      return sanitizedObject;
    };

    return sanitize(eventProperties);
  }

  sendTabViewEvent(groupName, tabs, selectedTab) {
    const tab: string = tabs[selectedTab];
    const eventProperties = {
      Grupo: this.groupName[groupName],
      Aba: tab,
    };
    this.sendEvent('Viu Aba', eventProperties);
  }

  sendSubTabViewEvent(groupName, tabName, subTabs, selectedSubTab, subTabNameKey) {
    const subTab: string = subTabs.find((subTab) => subTab.id === selectedSubTab);
    const subTabName = subTab ? subTab[subTabNameKey] || '' : '';
    let eventProperties = {};
    if (this.translate.currentLang === 'pt-br') {
      eventProperties = {
        Grupo: this.groupName[groupName],
        Aba: this.translate.instant(tabName),
        'Sub-Aba': this.translate.instant(subTabName),
      };
    } else {
      const currentLang = this.translate.currentLang;
      this.translate.use('pt-br');
      eventProperties = {
        Grupo: this.groupName[groupName],
        Aba: this.translate.instant(tabName),
        'Sub-Aba': this.translate.instant(subTabName),
      };
      this.translate.use(currentLang);
    }
    this.sendEvent('Viu Sub-Aba', eventProperties);
  }

  sendDatePickerEvent(groupName: string, tabName: string, period: string) {
    let eventProperties = {};
    if (this.translate.currentLang === 'pt-br') {
      eventProperties = {
        Grupo: this.groupName[groupName],
        Aba: this.translate.instant(tabName),
        Período: period,
      };
    } else {
      const currentLang = this.translate.currentLang;
      this.translate.use('pt-br');
      eventProperties = {
        Grupo: this.groupName[groupName],
        Aba: this.translate.instant(tabName),
        Período: period,
      };
      this.translate.use(currentLang);
    }
    this.sendEvent('Alterou Calendário', eventProperties);
  }

  sendLayerStatusEvent(eventName: string, lgroup: any, candidateLayer: any) {
    const eventProperties = {
      Grupo: this.layerGroupIdToGroupName[lgroup.groupId],
      Subgrupo: this.translate.instant(lgroup.groupTitle),
      Camada: this.translate.instant(candidateLayer.legend),
    };
    this.sendEvent(eventName, eventProperties);
  }

  isUgm() {
    if (this.accessControl.isUgmAmplitude()) {
      return 'Sim';
    }
    return 'Não';
  }
}
